/**
 * 依賴箭頭的正交折線路由。
 *
 * 走線規則（與 MS Project 一致）：
 * - 一律先沿「離開方向」水平走出 GAP，避免線頭黏在任務條上。
 * - 只走水平／垂直線段，不使用斜線。
 * - 若可以直接以 L 形進入後續任務（同方向且不會逆穿任務條），走兩段式最短路徑；
 *   否則改走「列間帶」(band)：先繞到兩列之間的空白帶，再水平移動到進入點正上／下方。
 * - 進入點永遠留 GAP，箭頭方向 = 進入方向。
 */

import type { DependencyType } from '@bigint-project/core';

import type { LinkAnchor } from './types.js';

/** 線頭／線尾預留的水平間距。 */
export const ROUTE_GAP = 10;
/** 箭頭尺寸。 */
export const ARROW_SIZE = 5;

export interface Point {
  readonly x: number;
  readonly y: number;
}

export type Direction = 1 | -1;

export interface DependencyRoute {
  readonly points: readonly Point[];
  /** SVG path 的 d 屬性。 */
  readonly d: string;
  /** 箭頭進入方向：1 = 由左向右射入，-1 = 由右向左射入。 */
  readonly arrowDirection: Direction;
  /** 箭頭三角形的 points 字串。 */
  readonly arrowPoints: string;
  /** 是否走了列間帶繞路。 */
  readonly routedAround: boolean;
}

/** 依賴型別 → 前置任務的出線端點。 */
export const predecessorAnchorOf = (type: DependencyType): LinkAnchor =>
  type === 'FS' || type === 'FF' ? 'finish' : 'start';

/** 依賴型別 → 後續任務的入線端點。 */
export const successorAnchorOf = (type: DependencyType): LinkAnchor =>
  type === 'FS' || type === 'SS' ? 'start' : 'finish';

/** 由端點推導離開方向：自 finish 出線往右、自 start 出線往左。 */
export const exitDirectionOf = (anchor: LinkAnchor): Direction =>
  anchor === 'finish' ? 1 : -1;

/** 由端點推導進入方向：射入 start 為向右、射入 finish 為向左。 */
export const entryDirectionOf = (anchor: LinkAnchor): Direction =>
  anchor === 'start' ? 1 : -1;

export interface RouteParams {
  /** 前置任務出線點（依 type 為條的左緣或右緣）。 */
  readonly from: Point;
  /** 後續任務入線點。 */
  readonly to: Point;
  readonly type: DependencyType;
  readonly rowHeight: number;
  readonly gap?: number;
}

/** 移除連續重複點，避免產生零長度線段。 */
function dedupe(points: readonly Point[]): Point[] {
  const result: Point[] = [];
  for (const point of points) {
    const last = result[result.length - 1];
    if (last !== undefined && last.x === point.x && last.y === point.y) continue;
    result.push(point);
  }
  return result;
}

function toPathData(points: readonly Point[]): string {
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${round(point.x)} ${round(point.y)}`)
    .join(' ');
}

const round = (value: number): number => Math.round(value * 100) / 100;

function arrowPointsFor(tip: Point, direction: Direction): string {
  const back = tip.x - direction * ARROW_SIZE;
  return [
    `${round(tip.x)},${round(tip.y)}`,
    `${round(back)},${round(tip.y - ARROW_SIZE)}`,
    `${round(back)},${round(tip.y + ARROW_SIZE)}`,
  ].join(' ');
}

/**
 * 產生一條依賴線的正交路徑。
 *
 * `from` / `to` 由呼叫端依 `type` 取得正確的端點座標（見 predecessorAnchorOf /
 * successorAnchorOf），本函式只負責走線。
 */
export function routeDependency(params: RouteParams): DependencyRoute {
  const { from, to, type, rowHeight } = params;
  const gap = params.gap ?? ROUTE_GAP;
  const exitDir = exitDirectionOf(predecessorAnchorOf(type));
  const entryDir = entryDirectionOf(successorAnchorOf(type));

  const exitPoint: Point = { x: from.x + exitDir * gap, y: from.y };
  const approach: Point = { x: to.x - entryDir * gap, y: to.y };

  const sameRow = from.y === to.y;
  // 直接 L 形：從 exitPoint 垂直走到後續列，再水平射入。
  // 條件是水平段的方向與進入方向一致，否則會從任務條背面穿過去。
  const canRouteDirect = !sameRow && entryDir * (to.x - exitPoint.x) >= gap;

  let points: Point[];
  let routedAround: boolean;
  if (canRouteDirect) {
    points = dedupe([from, exitPoint, { x: exitPoint.x, y: to.y }, to]);
    routedAround = false;
  } else {
    const downward = sameRow ? -1 : to.y > from.y ? 1 : -1;
    const bandY = from.y + downward * (rowHeight / 2);
    points = dedupe([
      from,
      exitPoint,
      { x: exitPoint.x, y: bandY },
      { x: approach.x, y: bandY },
      approach,
      to,
    ]);
    routedAround = true;
  }

  return {
    points,
    d: toPathData(points),
    arrowDirection: entryDir,
    arrowPoints: arrowPointsFor(to, entryDir),
    routedAround,
  };
}

/** 檢查一條路徑是否完全由水平／垂直線段構成（測試輔助，也可用於除錯）。 */
export function isOrthogonal(points: readonly Point[]): boolean {
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (a === undefined || b === undefined) return false;
    if (a.x !== b.x && a.y !== b.y) return false;
  }
  return true;
}
